import { useState, useEffect } from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalClose,
  Option,
  Select,
  Sheet,
  Switch,
  Textarea,
  Typography,
} from "@mui/joy";
import { useMemo } from "react";
import {
  buildProviderCredentialPayload,
  getProviderLabel,
  KNOWN_PROVIDER_OPTIONS,
  normalizeProviderKey,
} from "../../../helpers/modelProviders";

const EditModelModal = ({ open, onClose, model, onUpdateModel }) => {
  const [formData, setFormData] = useState({
    name: "",
    display_name: "",
    provider: "openai",
    api_key: "",
    aws_access_key: "",
    aws_secret_key: "",
    aws_region: "",
    deployment_url: "",
    google_service_account_json: "",
    is_active: true,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const providerOptions = useMemo(() => {
    const options = [...KNOWN_PROVIDER_OPTIONS];
    if (model?.provider && !options.some((option) => option.value === normalizeProviderKey(model.provider))) {
      options.push({
        value: normalizeProviderKey(model.provider),
        label: getProviderLabel(model.provider),
      });
    }
    return options;
  }, [model?.provider]);

  useEffect(() => {
    if (model && open) {
      setFormData({
        name: model.name || "",
        display_name: model.display_name || "",
        provider: model.provider || "openai",
        api_key: "",
        aws_access_key: "",
        aws_secret_key: "",
        aws_region: model.aws_region || "",
        deployment_url: model.deployment_url || "",
        google_service_account_json: "",
        is_active: model.is_active ?? true,
      });
    }
  }, [model, open]);

  const handleChange = (field) => (e) => {
    const value = e?.target?.value ?? e;
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSwitchChange = (field) => (e) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.checked }));
  };

  const handleClose = () => {
    onClose();
  };

  const buildPayload = () => {
    const payload = {
      name: formData.name,
      display_name: formData.display_name,
      is_active: formData.is_active,
    };

    Object.assign(payload, buildProviderCredentialPayload({
      provider: formData.provider,
      values: formData,
      operation: "update",
    }));

    return payload;
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      return;
    }

    setIsSubmitting(true);
    const payload = buildPayload();
    const result = await onUpdateModel(model.id, payload);
    setIsSubmitting(false);

    if (result.success) {
      handleClose();
    }
  };

  const renderProviderFields = () => {
    switch (normalizeProviderKey(formData.provider)) {
      case "openai":
        return (
          <FormControl sx={{ mb: 2 }}>
            <FormLabel>API Key</FormLabel>
            <Input
              type="password"
              placeholder="Leave empty to keep unchanged"
              value={formData.api_key}
              onChange={handleChange("api_key")}
            />
          </FormControl>
        );

      case "bedrock":
        return (
          <>
            <FormControl sx={{ mb: 2 }}>
              <FormLabel>AWS Access Key</FormLabel>
              <Input
                placeholder="Leave empty to keep unchanged"
                value={formData.aws_access_key}
                onChange={handleChange("aws_access_key")}
              />
            </FormControl>
            <FormControl sx={{ mb: 2 }}>
              <FormLabel>AWS Secret Key</FormLabel>
              <Input
                type="password"
                placeholder="Leave empty to keep unchanged"
                value={formData.aws_secret_key}
                onChange={handleChange("aws_secret_key")}
              />
            </FormControl>
            <FormControl sx={{ mb: 2 }}>
              <FormLabel>AWS Region</FormLabel>
              <Input
                placeholder="us-east-1"
                value={formData.aws_region}
                onChange={handleChange("aws_region")}
              />
            </FormControl>
          </>
        );

      case "azure":
        return (
          <>
            <FormControl sx={{ mb: 2 }}>
              <FormLabel>API Key</FormLabel>
              <Input
                type="password"
                placeholder="Leave empty to keep unchanged"
                value={formData.api_key}
                onChange={handleChange("api_key")}
              />
            </FormControl>
            <FormControl sx={{ mb: 2 }}>
              <FormLabel>Deployment URL</FormLabel>
              <Input
                placeholder="https://myresource.openai.azure.com"
                value={formData.deployment_url}
                onChange={handleChange("deployment_url")}
              />
            </FormControl>
          </>
        );

      case "azure_document_intelligence":
        return (
          <>
            <FormControl sx={{ mb: 2 }}>
              <FormLabel>API Key</FormLabel>
              <Input
                type="password"
                placeholder="Leave empty to keep unchanged"
                value={formData.api_key}
                onChange={handleChange("api_key")}
              />
            </FormControl>
            <FormControl sx={{ mb: 2 }}>
              <FormLabel>Deployment URL</FormLabel>
              <Input
                placeholder="https://<resource>.cognitiveservices.azure.com/"
                value={formData.deployment_url}
                onChange={handleChange("deployment_url")}
              />
            </FormControl>
          </>
        );

      case "google_vision":
        return (
          <FormControl sx={{ mb: 2 }}>
            <FormLabel>Google Service Account JSON</FormLabel>
            <Textarea
              minRows={6}
              placeholder={'Leave empty to keep unchanged'}
              value={formData.google_service_account_json}
              onChange={handleChange("google_service_account_json")}
            />
          </FormControl>
        );

      case "ollama":
      case "lm_studio":
        return (
          <FormControl sx={{ mb: 2 }}>
            <FormLabel>Deployment URL</FormLabel>
            <Input
              placeholder={normalizeProviderKey(formData.provider) === "ollama" ? "http://localhost:11434" : "http://localhost:1234/v1"}
              value={formData.deployment_url}
              onChange={handleChange("deployment_url")}
            />
          </FormControl>
        );

      default:
        return null;
    }
  };

  if (!model) return null;

  return (
    <Modal
      aria-labelledby="edit-model-modal-title"
      open={open}
      onClose={handleClose}
      sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}
    >
      <Sheet
        variant="outlined"
        sx={{ maxWidth: 500, width: "100%", borderRadius: "md", p: 3, boxShadow: "lg", bgcolor: "background.surface" }}
      >
        <ModalClose variant="plain" sx={{ m: 1 }} onClick={handleClose} />
        <Typography
          component="h2"
          id="edit-model-modal-title"
          level="h4"
          textColor="inherit"
          sx={{ fontWeight: "lg", mb: 1 }}
        >
          Edit Model
        </Typography>
        <Typography textColor="text.tertiary" sx={{ mb: 2 }}>
          Update model configuration. Credentials are write-only.
        </Typography>

        <FormControl sx={{ mb: 2 }}>
          <FormLabel>Provider</FormLabel>
          <Select value={normalizeProviderKey(formData.provider)} disabled>
            {providerOptions.map((p) => (
              <Option key={p.value} value={p.value}>
                {p.label}
              </Option>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ mb: 2 }}>
          <FormLabel>Model Name</FormLabel>
          <Input
            placeholder="e.g., gpt-4o"
            value={formData.name}
            onChange={handleChange("name")}
          />
        </FormControl>

        <FormControl sx={{ mb: 2 }}>
          <FormLabel>Display Name</FormLabel>
          <Input
            placeholder="e.g., GPT-4o"
            value={formData.display_name}
            onChange={handleChange("display_name")}
          />
        </FormControl>

        {renderProviderFields()}

        <FormControl
          orientation="horizontal"
          sx={{ mb: 2, justifyContent: "space-between" }}
        >
          <FormLabel>Active</FormLabel>
          <Switch
            checked={formData.is_active}
            onChange={handleSwitchChange("is_active")}
          />
        </FormControl>

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 2 }}>
          <Button onClick={handleClose} variant="plain">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="solid"
            color="primary"
            loading={isSubmitting}
            disabled={!formData.name.trim()}
          >
            Save Changes
          </Button>
        </Box>
      </Sheet>
    </Modal>
  );
};

export default EditModelModal;
